/**
 * O "LGC disponível" do rascunho contra o oficial — a regra, pura e testada.
 *
 * ⚠ DESDE A 0032 (cadastro por cenário, 12/10/2026) ESTA REGRA VALE SÓ PARA O RASCUNHO DO FORMATO 1
 * (gravado antes da 0032) — decisão D25 = A, até a 0033. O LGC passou a ser de cada cenário e só
 * muda publicando aquele cenário (que sobe a versão e vence o rascunho): não há mais "outra porta",
 * e o rascunho do formato 2 vence em tudo, menos o nome (`telas/cenario.ts`). Quando não houver
 * mais rascunho do formato 1, este arquivo e o teste dele saem.
 *
 * LOTE 4 DE 10/10/2026, item 6. Pedido literal: "O que vc quer dizer com valor digitado e não
 * publicado?" — explicado (o digitado vai para o rascunho e só vira oficial ao publicar; ao
 * reabrir sem publicar, o app trocava pelo oficial sem avisar) — e a decisão:
 * "Fica o digitado (Recomendado)".
 *
 * ANTES: ao abrir com rascunho, a tela do cenário SOBREPUNHA o LGC do rascunho pelo oficial,
 * calada. O motivo era bom e continua valendo: o LGC também muda por OUTRA porta (o cadastro
 * da incorporadora, de onde ele é herdado), e um rascunho não pode esconder essa mudança —
 * ele limita o teto do PJ, então seria número errado, não detalhe. O defeito era o "calada":
 * o Verus/Realista tinha rascunho com R$ 5 mi e oficial R$ 4 mi, e a tela mostrava 4 mi.
 *
 * AGORA: o do rascunho FICA. Quando ele é diferente do oficial, a tela pergunta, dizendo os
 * dois valores, qual manter — nada fica escondido: nem o digitado (que antes sumia), nem a
 * mudança que veio de outra porta (que é o que a sobreposição protegia). Sem diferença,
 * nada aparece.
 *
 * O NOME do empreendimento continua vindo SEMPRE do publicado (só muda por "Renomear").
 *
 * REVISÃO DO LOTE 4 (10/10/2026) — a fotografia não é o digitado. O rascunho guarda o
 * empreendimento INTEIRO a cada edição, então o LGC entra nele mesmo quando ninguém o digitou.
 * Com outro cenário do empreendimento publicando um LGC novo, o diálogo dizia "Manter o que
 * digitei (R$ 4 mi)" — e Enter/Esc/✕ mantinham a fotografia velha, que a próxima publicação
 * gravava como o LGC de todos os cenários (reproduzido no navegador). Agora o rascunho guarda o
 * LGC OFICIAL de quando a sessão começou (`lgcOficialNaOrigem`, em packages/dados/src/rascunho.ts):
 *   · igual a ele (`fotografia`): ninguém digitou — vale o oficial atual, calado (como até o lote 3);
 *   · diferente (`digitado`): pergunta como antes, e "Manter o que digitei" é verdade;
 *   · rascunho antigo, sem o campo (`desconhecida`): pergunta com rótulo neutro ("Manter o do
 *     rascunho"), e o caminho padrão (foco, Esc, ✕, clique fora) fica com o OFICIAL.
 */

/** Os dois valores, quando o rascunho e o oficial discordam. `null` = nada a perguntar. */
export interface DivergenciaLgc {
  /** o que está no rascunho (digitado e ainda não publicado) */
  doRascunho: number;
  /** o oficial: o LGC DESTE CENÁRIO (`app.cenario.lgc_disponivel`, desde a 0032 — APC-5) */
  oficial: number;
}

const centavos = (v: number): number => Math.round(v * 100);

/**
 * Compara em CENTAVOS: o campo mostra 2 casas e a coluna do banco também; diferença menor que
 * um centavo não é algo que a pessoa possa ter digitado, e perguntar por ela seria ruído.
 * Rascunho sem o campo (formato antigo) ou com valor que não é número: não há o que perguntar
 * — vale o oficial, como antes.
 */
export function divergenciaLgc(doRascunho: unknown, oficial: unknown): DivergenciaLgc | null {
  if (typeof doRascunho !== 'number' || !Number.isFinite(doRascunho)) return null;
  const of = typeof oficial === 'number' && Number.isFinite(oficial) ? oficial : 0;
  if (centavos(doRascunho) === centavos(of)) return null;
  return { doRascunho, oficial: of };
}

/**
 * O cadastro com que a tela abre: o do rascunho, com o NOME do publicado e o LGC escolhido.
 * `usarOficial` = a pessoa escolheu o oficial (ou não havia divergência — então tanto faz).
 */
export function cadastroAoAbrir<T extends { nome: string; lgcDisponivel: number }>(
  doRascunho: T,
  publicado: { nome: string; lgcDisponivel: number },
  usarOficial: boolean,
): T {
  const div = divergenciaLgc(doRascunho.lgcDisponivel, publicado.lgcDisponivel);
  return {
    ...doRascunho,
    nome: publicado.nome,
    lgcDisponivel: div !== null && !usarOficial ? div.doRascunho : publicado.lgcDisponivel,
  };
}

/** De onde veio o LGC do rascunho (revisão do lote 4) — ver o cabeçalho. */
export type OrigemLgc = 'digitado' | 'fotografia' | 'desconhecida';

/**
 * `oficialNaOrigem` = o LGC oficial quando a sessão que gravou o rascunho começou. Compara em
 * centavos, como `divergenciaLgc`. Sem a origem (rascunho antigo, ou valor que não é número):
 * `desconhecida` — não dá para afirmar que alguém digitou.
 */
export function origemDoLgc(doRascunho: unknown, oficialNaOrigem: unknown): OrigemLgc {
  if (typeof oficialNaOrigem !== 'number' || !Number.isFinite(oficialNaOrigem)) return 'desconhecida';
  if (typeof doRascunho !== 'number' || !Number.isFinite(doRascunho)) return 'desconhecida';
  return centavos(doRascunho) === centavos(oficialNaOrigem) ? 'fotografia' : 'digitado';
}

/** A decisão do LGC ao abrir o cenário (D25 = A). */
export interface LgcAoAbrir {
  /** o que perguntar; `null` = nada (formato 2, sem rascunho, sem divergência, fotografia, sem poder) */
  emDisputa: DivergenciaLgc | null;
  /** de onde veio o LGC do rascunho, quando houve divergência a julgar (só no formato 1) */
  origem: OrigemLgc | null;
  /** vale o oficial, calado: formato 1 com o LGC do rascunho igual à fotografia da origem */
  calado: boolean;
}

/**
 * D25 = A (12/10/2026): a regra do lote 4 vale SÓ para o rascunho do FORMATO 1 (gravado antes da
 * 0032), e só para quem pode editar um cenário ativo. No formato 2 (e sem rascunho) não há o que
 * perguntar nem o que calar: o rascunho vence em tudo, inclusive no LGC (`cadastroAoAbrir` com
 * `usarOficial = false`). Antes esta condição morava solta na tela, sem prova.
 */
export function lgcAoAbrir(p: {
  formato: 1 | 2 | null;
  podePerguntar: boolean;
  doRascunho: unknown;
  oficial: unknown;
  oficialNaOrigem: unknown;
}): LgcAoAbrir {
  const nada: LgcAoAbrir = { emDisputa: null, origem: null, calado: false };
  if (p.formato !== 1 || !p.podePerguntar) return nada;
  const div = divergenciaLgc(p.doRascunho, p.oficial);
  if (div === null) return nada;
  const origem = origemDoLgc(div.doRascunho, p.oficialNaOrigem);
  return origem === 'fotografia' ? { emDisputa: null, origem, calado: true } : { emDisputa: div, origem, calado: false };
}

/**
 * Os textos do diálogo. Sem ID de elemento (regra da v65): o campo pelo NOME. "Manter o que
 * digitei" só quando se SABE que foi digitado; sem saber, "Manter o do rascunho".
 */
export function textosDivergenciaLgc(d: DivergenciaLgc, moeda: (v: number) => string, origem: OrigemLgc): {
  titulo: string; corpo: string; manter: string; usarOficial: string;
} {
  const digitado = origem === 'digitado';
  /* APC-5 (revisão de 12/10/2026): "deste cenário", e não "deste empreendimento" — desde a 0032 o oficial
     é o do cenário, e escolher um ou outro não mexe nos irmãos (a dica do campo, EL-130, já dizia assim). */
  return {
    titulo: 'LGC disponível diferente do oficial',
    /* revisão de 12/10/2026: o oficial é o publicado DESTE CENÁRIO (desde a 0032 o LGC é de cada cenário) */
    corpo: digitado
      ? `O “LGC disponível” que você digitou e ainda não publicou (${moeda(d.doRascunho)}) é diferente do valor oficial deste `
        + `cenário (${moeda(d.oficial)}). Ele só vira oficial quando você publicar.`
      : `O “LGC disponível” guardado no seu rascunho (${moeda(d.doRascunho)}) é diferente do valor oficial deste `
        + `cenário (${moeda(d.oficial)}). Ele só vira oficial quando você publicar. Se você não digitou este valor, `
        + 'o oficial mudou depois do seu rascunho — use o oficial.',
    manter: digitado ? `Manter o que digitei (${moeda(d.doRascunho)})` : `Manter o do rascunho (${moeda(d.doRascunho)})`,
    usarOficial: `Usar o oficial (${moeda(d.oficial)})`,
  };
}
